import { z } from "zod";

export * from "./preferences";

export const AssignmentIdSchema = z.string().brand("AssignmentId");
export type AssignmentId = z.infer<typeof AssignmentIdSchema>;

export const AssignmentStatusSchema = z.enum([
  "draft",
  "queued",
  "processing",
  "completed",
  "failed",
]);
export type AssignmentStatus = z.infer<typeof AssignmentStatusSchema>;

/**
 * Criteria category enum for highlight colors.
 * Each category maps to a distinct, colorblind-accessible color.
 */
export const CriteriaCategorySchema = z.enum([
  'claims',
  'support',
  'reasoning',
  'flow',
  'style',
  'mechanics',
  'other',
]);
export type CriteriaCategory = z.infer<typeof CriteriaCategorySchema>;

export const AssignmentCriterionSchema = z.object({
  id: z.string(),
  key: z.string(),
  label: z.string().min(1),
  description: z.string().nullable().optional(),  // Human-facing description (for students)
  llmDescription: z.string().nullable().optional(),  // AI-facing description
  action: z.string().nullable().optional(),  // Optional: action type for generation (create_new, matches_existing, suggest_update)
  category: CriteriaCategorySchema.nullable().optional(),  // Category for highlight colors
});
export type AssignmentCriterion = z.infer<typeof AssignmentCriterionSchema>;

export const AssignmentCriteriaSchema = z.array(AssignmentCriterionSchema);
export type AssignmentCriteria = z.infer<typeof AssignmentCriteriaSchema>;

export const MatchingTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  isDefault: z.boolean(),
});
export type MatchingTemplate = z.infer<typeof MatchingTemplateSchema>;

// Note: "drive" source was removed in October 2025.
// "text" = teacher-pasted document, "student_writing" = student wrote directly in Markeroo
// "extension" = imported from Chrome extension (AI Detector)
export const DocumentSourceSchema = z.enum(["text", "student_writing", "extension"]);
export type DocumentSource = z.infer<typeof DocumentSourceSchema>;

export const DocumentStatusSchema = z.enum(["draft", "final"]);
export type DocumentStatus = z.infer<typeof DocumentStatusSchema>;

export const DocumentVersionStatusSchema = z.enum(["writing", "submitted", "feedback_shared"]);
export type DocumentVersionStatus = z.infer<typeof DocumentVersionStatusSchema>;

export const PipelineRunStatusSchema = z.enum(["pending", "processing", "completed", "failed"]);
export type PipelineRunStatus = z.infer<typeof PipelineRunStatusSchema>;

export const PaperTypeSchema = z.enum([
  "opinion_argument",
  "informative_explanatory",
  "narrative",
  "other",
]);
export type PaperType = z.infer<typeof PaperTypeSchema>;

export const AssignmentSchema = z.object({
  id: AssignmentIdSchema,
  classId: z.string().uuid(),
  title: z.string().min(1),
  description: z.string().nullable(),
  status: AssignmentStatusSchema.nullable(), // Null = ready/neutral state, no badge
  defaultDocumentStatus: DocumentStatusSchema,
  paperType: PaperTypeSchema.nullable(),
  criteria: AssignmentCriteriaSchema,
  archived: z.boolean().default(false),
  createdAt: z.string(),
  updatedAt: z.string(),
  criteriaUpdatedAt: z.string().nullable().optional(),
  criteriaAreCustom: z.boolean().default(false),
  matchingTemplate: MatchingTemplateSchema.nullable().optional(),
  totalSubmissions: z.number().int().nonnegative().default(0),
  unassessedSubmissions: z.number().int().nonnegative().default(0),
  sharedSubmissions: z.number().int().nonnegative().default(0),
  totalStudents: z.number().int().nonnegative().default(0),
  studentsWithSubmissions: z.number().int().nonnegative().default(0),
  emailTemplateId: z.string().uuid().nullable().optional(),
  // Google Classroom fields
  gcCourseWorkId: z.string().nullable().optional(),
  // Word count requirements
  minWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
  maxWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
});
export type Assignment = z.infer<typeof AssignmentSchema>;

export const CreateAssignmentInputSchema = z.object({
  classId: z.string().uuid(),
  title: z.string().min(1, "Title required"),
  description: z.string().optional(),
  paperType: PaperTypeSchema,
  criteria: AssignmentCriteriaSchema.optional(),
  skipDefaultCriteria: z.boolean().optional().default(false),
  // Word count requirements
  minWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
  maxWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
});
// Use z.input for the input type since we have defaults
export type CreateAssignmentInput = z.input<typeof CreateAssignmentInputSchema>;

export const UpdateAssignmentInputSchema = z.object({
  title: z.string().optional(),
  description: z.string().nullable().optional(),
  status: AssignmentStatusSchema.optional(),
  classId: z.string().uuid().optional(),
  archived: z.boolean().optional(),
  paperType: PaperTypeSchema.optional(),
  emailTemplateId: z.string().uuid().nullable().optional(),
  // Word count requirements
  minWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
  maxWordCount: z.number().int().nonnegative().max(50000).nullable().optional(),
});
export type UpdateAssignmentInput = z.infer<typeof UpdateAssignmentInputSchema>;

export const PaginatedAssignmentsSchema = z.object({
  items: z.array(AssignmentSchema),
  nextCursor: AssignmentIdSchema.nullable(),
});
export type PaginatedAssignments = z.infer<typeof PaginatedAssignmentsSchema>;

export const AuthUserSchema: z.ZodType<{
  id: string;
  email: string;
  fullName: string;
  preferredName?: string | null;
  pictureUrl?: string | null;
  createdAt: string;
  isSuperuser?: boolean;
  isInternal?: boolean;
  preferences?: Record<string, unknown> | null;
  gcMode?: boolean;
  studentWritingEnabled?: boolean;
  dpaAcceptedAt?: string | null;
  dpaVersion?: string | null;
  impersonator?: {
    id: string;
    email: string;
    fullName: string;
    preferredName?: string | null;
    pictureUrl?: string | null;
    createdAt: string;
    isSuperuser?: boolean;
    isInternal?: boolean;
    preferences?: Record<string, unknown> | null;
    gcMode?: boolean;
    dpaAcceptedAt?: string | null;
    dpaVersion?: string | null;
  } | null;
}> = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  preferredName: z.string().nullable().optional(),
  pictureUrl: z.string().url().nullable().optional(),
  createdAt: z.string(),
  isSuperuser: z.boolean().optional().default(false),
  isInternal: z.boolean().optional().default(false),
  preferences: z.record(z.unknown()).nullable().optional(),
  gcMode: z.boolean().optional().default(false),
  studentWritingEnabled: z.boolean().optional().default(false),
  dpaAcceptedAt: z.string().nullable().optional(),
  dpaVersion: z.string().nullable().optional(),
  impersonator: z.lazy(() => AuthUserSchema).nullable().optional(),
});
export type AuthUser = z.infer<typeof AuthUserSchema>;

export const AuthResponseSchema = z.object({
  accessToken: z.string().min(1),
  tokenType: z.literal("bearer"),
  user: AuthUserSchema,
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const DocumentSchema = z.object({
  id: z.string(),
  assignmentId: z.string(),
  ownerId: z.string(),
  studentId: z.string().uuid(),
  studentName: z.string(),
  studentUpdatedAt: z.string(),
  source: DocumentSourceSchema,
  bodyJson: z.record(z.string(), z.unknown()).nullable(),
  bodyText: z.string().nullable(),
  status: DocumentStatusSchema,
  issueCount: z.number().int().nonnegative().nullable().optional(),
  createdAt: z.string(),
  hasAssessment: z.boolean(),
  needsRegradeReason: z.array(z.object({
    reason: z.string(),
    timestamp: z.string(),
    metadata: z.record(z.unknown()),
  })).nullable().optional(),
  shareToken: z.string().nullable().optional(),
  shareCreatedAt: z.string().nullable().optional(),
  lastPipelineStatus: PipelineRunStatusSchema.nullable().optional(),
  emailBounced: z.boolean().default(false),
  bounceReason: z.string().nullable().optional(),
  // Google Classroom fields
  gcSubmissionId: z.string().nullable().optional(),
});
export type Document = z.infer<typeof DocumentSchema>;

export const DocumentListSchema = z.object({
  items: z.array(DocumentSchema),
});
export type DocumentList = z.infer<typeof DocumentListSchema>;

export const CreateDocumentInputSchema = z.object({
  studentId: z.string().uuid(),
  source: DocumentSourceSchema,
  bodyJson: z.any().nullable().optional(),
  status: DocumentStatusSchema.optional().default("draft"),
  autoQueue: z.boolean().optional().default(true),
});
export type CreateDocumentInput = z.infer<typeof CreateDocumentInputSchema>;

export const UpdateDocumentInputSchema = z.object({
  bodyJson: z.any().nullable().optional(),
  studentId: z.string().uuid().optional(),
  status: DocumentStatusSchema.optional(),
  autoQueue: z.boolean().optional(),
});
export type UpdateDocumentInput = z.infer<typeof UpdateDocumentInputSchema>;

export const BillingIntervalSchema = z.enum(["month", "year"]);
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

export const UsageSummarySchema = z.object({
  plan: z.string(),
  planLabel: z.string(),
  docsLimit: z.number().int(),  // Can be -1 for unlimited
  docsUsed: z.number().int().nonnegative(),
  docsRemaining: z.number().int().nonnegative(),
  usagePercent: z.number().min(0),
  nearLimit: z.boolean(),
  overLimit: z.boolean(),
  upgradeUrl: z.string().url().nullable().optional(),
  hasActiveSubscription: z.boolean(),
  subscriptionStatus: z.string().nullable().optional(),
  currentPeriodEnd: z.string().nullable().optional(),
  billingInterval: BillingIntervalSchema.nullable().optional(),
  // Trial fields
  trialActive: z.boolean().optional(),
  trialEndsAt: z.string().nullable().optional(),
  daysRemainingInTrial: z.number().int().nullable().optional(),
});
export type UsageSummary = z.infer<typeof UsageSummarySchema>;

export const BillingCheckoutSessionSchema = z.object({
  url: z.string().url(),
});
export type BillingCheckoutSession = z.infer<typeof BillingCheckoutSessionSchema>;

export const BillingPortalSessionSchema = z.object({
  url: z.string().url(),
});
export type BillingPortalSession = z.infer<typeof BillingPortalSessionSchema>;

export const ShareTagSchema = z.object({
  tag_id: z.string(),
  startPath: z.array(z.number().int().nonnegative()).min(1),
  startChar: z.number().int().nonnegative(),
  endPath: z.array(z.number().int().nonnegative()).min(1),
  endChar: z.number().int().nonnegative(),
  explanation: z.string(),
  suggestion: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  humanDescription: z.string().nullable().optional(),
  display_style: z.enum(["demonstrative", "full"]),  // Required: LLM must provide this
  beforeContext: z.string().nullable().optional(),
  phrase: z.string().nullable().optional(),
  afterContext: z.string().nullable().optional(),
});
export type ShareTag = z.infer<typeof ShareTagSchema>;

export const ShareSummarySchema = z.object({
  celebrate: z.string(),
  priorities: z.string(),
  strategies: z.string(),
});
export type ShareSummary = z.infer<typeof ShareSummarySchema>;

export const ShareTagDefinitionSchema = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string(),
  category: CriteriaCategorySchema.optional(),
  humanDescription: z.string().nullable().optional(),
  llmDescription: z.string().nullable().optional(),
});
export type ShareTagDefinition = z.infer<typeof ShareTagDefinitionSchema>;

export const TeacherCommentSchema = z.object({
  id: z.string(),
  startPath: z.array(z.number().int().nonnegative()).min(1),
  startChar: z.number().int().nonnegative(),
  endPath: z.array(z.number().int().nonnegative()).min(1),
  endChar: z.number().int().nonnegative(),
  comment_text: z.string(),
  color: z.string(),
  category: CriteriaCategorySchema.optional(), // Category for the comment (derives color)
  teacher_name: z.string(),
  created_by: z.string().uuid(),
  created_at: z.string(),
  phrase: z.string(),
  before_context: z.string().nullable().optional(),
  after_context: z.string().nullable().optional(),
});
export type TeacherComment = z.infer<typeof TeacherCommentSchema>;

export const ShareDocumentSchema = z.object({
  assignmentTitle: z.string(),
  summary: ShareSummarySchema,
  tags: z.array(ShareTagSchema),
  criteriaTemplateNotes: z.string().nullable().optional(),
  bodyJson: z.record(z.string(), z.unknown()).nullable().optional(),
  annotatedDocument: z.string().nullable().optional(),
  tagPalette: z.array(ShareTagDefinitionSchema),
  removedLabelIds: z.array(z.string()).nullable().optional(),
  teacherComments: z.array(TeacherCommentSchema).nullable().optional(),
  schemaVersion: z.string(),
  promptVersion: z.string().nullable().optional(),
  generatedAt: z.string(),
  showAllMarkup: z.boolean().default(true),
  completedHighlightIds: z.array(z.string()).nullable().optional(),
});
export type ShareDocument = z.infer<typeof ShareDocumentSchema>;

export const ShareWithProgressionSchema = z.object({
  sharedDocument: ShareDocumentSchema,
  documents: z.array(DocumentSchema),
  studentName: z.string(),
  assignmentTitle: z.string(),
  documentAssessments: z.record(z.string(), ShareDocumentSchema).optional(),
});
export type ShareWithProgression = z.infer<typeof ShareWithProgressionSchema>;

export const DocumentSummaryUpdateSchema = z.object({
  celebrate: z.string().max(3000).nullable().optional(),
  priorities: z.string().max(3000).nullable().optional(),
  strategies: z.string().max(3000).nullable().optional(),
});
export type DocumentSummaryUpdate = z.infer<typeof DocumentSummaryUpdateSchema>;

export const CreateTeacherCommentInputSchema = z.object({
  startPath: z.array(z.number().int().nonnegative()).min(1),
  startChar: z.number().int().nonnegative(),
  endPath: z.array(z.number().int().nonnegative()).min(1),
  endChar: z.number().int().nonnegative(),
  comment_text: z.string().min(1).max(1000),
  color: z.string(),
  phrase: z.string(),
  before_context: z.string().nullable().optional(),
  after_context: z.string().nullable().optional(),
});
export type CreateTeacherCommentInput = z.infer<typeof CreateTeacherCommentInputSchema>;

export const UpdateTeacherCommentInputSchema = z.object({
  comment_text: z.string().min(1).max(1000),
  color: z.string().optional(),
});
export type UpdateTeacherCommentInput = z.infer<typeof UpdateTeacherCommentInputSchema>;

export const DocumentMarkupSchema = ShareDocumentSchema;
export type DocumentMarkup = ShareDocument;

export const GradeLevelSchema = z.enum([
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
]);
export type GradeLevel = z.infer<typeof GradeLevelSchema>;

export const ClassSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string().min(1),
  defaultElaGradeLevel: GradeLevelSchema.nullable(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Google Classroom fields
  gcCourseId: z.string().nullable().optional(),
  gcCourseName: z.string().nullable().optional(),
  gcAlternateLink: z.string().nullable().optional(),
  gcLastSyncedAt: z.string().nullable().optional(),
});
export type Class = z.infer<typeof ClassSchema>;

export const CreateClassInputSchema = z.object({
  name: z.string().min(1, "Name required"),
  defaultElaGradeLevel: GradeLevelSchema,
});
export type CreateClassInput = z.infer<typeof CreateClassInputSchema>;

export const UpdateClassInputSchema = z.object({
  name: z.string().min(1).optional(),
  defaultElaGradeLevel: GradeLevelSchema.nullable().optional(),
  archived: z.boolean().optional(),
});
export type UpdateClassInput = z.infer<typeof UpdateClassInputSchema>;

export const PaginatedClassesSchema = z.object({
  items: z.array(ClassSchema),
  nextCursor: z.string().uuid().nullable(),
});
export type PaginatedClasses = z.infer<typeof PaginatedClassesSchema>;

export const StudentSchema = z.object({
  id: z.string().uuid(),
  classId: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string().min(1),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  elaGradeLevel: GradeLevelSchema,
  notes: z.string().nullable(),
  isEll: z.boolean().default(false),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Google Classroom fields
  gcUserId: z.string().nullable().optional(),
  // Student portal access
  portalKey: z.string().nullable().optional(),
});
export type Student = z.infer<typeof StudentSchema>;

export const CreateStudentInputSchema = z.object({
  name: z.string().min(1, "Name required"),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  elaGradeLevel: GradeLevelSchema.optional(),
  notes: z.string().nullable().optional(),
  isEll: z.boolean().optional().default(false),
});
export type CreateStudentInput = z.infer<typeof CreateStudentInputSchema>;

export const UpdateStudentInputSchema = z.object({
  name: z.string().min(1).optional(),
  firstName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  elaGradeLevel: GradeLevelSchema.optional(),
  notes: z.string().nullable().optional(),
  isEll: z.boolean().optional(),
  archived: z.boolean().optional(),
});
export type UpdateStudentInput = z.infer<typeof UpdateStudentInputSchema>;

export const PaginatedStudentsSchema = z.object({
  items: z.array(StudentSchema),
  nextCursor: z.string().uuid().nullable(),
});
export type PaginatedStudents = z.infer<typeof PaginatedStudentsSchema>;

// Criteria Generation Schemas
export const CriteriaGenerateRequestSchema = z.object({
  prompt: z.string().min(10).max(2000),
});
export type CriteriaGenerateRequest = z.infer<typeof CriteriaGenerateRequestSchema>;

export const CriteriaGenerateResponseSchema = z.object({
  criteria: z.array(AssignmentCriterionSchema).min(1).max(10),
});
export type CriteriaGenerateResponse = z.infer<typeof CriteriaGenerateResponseSchema>;

export const CriteriaBulkCreateRequestSchema = z.object({
  criteria: z.array(z.object({
    label: z.string().min(1),
    llmDescription: z.string(),
    humanDescription: z.string(),
    action: z.string().optional(),  // Optional: create_new, matches_existing, suggest_update
    key: z.string().optional(),  // Optional: for suggest_update action
  })).min(1),
});
export type CriteriaBulkCreateRequest = z.infer<typeof CriteriaBulkCreateRequestSchema>;

export const CriteriaBulkCreateFailureSchema = z.object({
  index: z.number().int().nonnegative(),
  label: z.string(),
  error: z.string(),
});
export type CriteriaBulkCreateFailure = z.infer<typeof CriteriaBulkCreateFailureSchema>;

export const CriteriaBulkCreateResponseSchema = z.object({
  created: z.array(AssignmentCriterionSchema),
  failed: z.array(CriteriaBulkCreateFailureSchema),
});
export type CriteriaBulkCreateResponse = z.infer<typeof CriteriaBulkCreateResponseSchema>;

// Feedback Schemas
export const FeedbackStatusSchema = z.enum(["new", "in_progress", "resolved", "wont_fix"]);
export type FeedbackStatus = z.infer<typeof FeedbackStatusSchema>;

// Email Schemas
export const EmailStatusSchema = z.enum(["pending", "sent", "delivered", "bounced", "failed"]);
export type EmailStatus = z.infer<typeof EmailStatusSchema>;

export const EmailTypeSchema = z.enum(["system", "feedback_thank_you", "feedback_reply", "feedback_inbound", "share_link"]);
export type EmailType = z.infer<typeof EmailTypeSchema>;

export const EmailListItemSchema = z.object({
  id: z.string().uuid(),
  emailType: EmailTypeSchema,
  toEmail: z.string(),
  fromEmail: z.string(),
  subject: z.string(),
  textContent: z.string().nullable(),
  status: EmailStatusSchema,
  relatedEntityType: z.string().nullable(),
  relatedEntityId: z.string().uuid().nullable(),
  threadId: z.string().uuid().nullable(),
  sentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EmailListItem = z.infer<typeof EmailListItemSchema>;

export const EmailListResponseSchema = z.object({
  items: z.array(EmailListItemSchema),
  nextCursor: z.string().uuid().nullable(),
});
export type EmailListResponse = z.infer<typeof EmailListResponseSchema>;

export const FeedbackUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
});
export type FeedbackUser = z.infer<typeof FeedbackUserSchema>;

// List item schema (simplified, no admin fields)
export const FeedbackListItemSchema = z.object({
  id: z.string().uuid(),
  user: FeedbackUserSchema.nullable(),
  description: z.string(),
  pageUrl: z.string().nullable(),
  status: FeedbackStatusSchema,
  attachmentCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeedbackListItem = z.infer<typeof FeedbackListItemSchema>;

// Full feedback item schema (with admin fields)
export const FeedbackItemSchema = z.object({
  id: z.string().uuid(),
  user: FeedbackUserSchema.nullable(),
  description: z.string(),
  pageUrl: z.string().nullable(),
  userAgent: z.string().nullable(),
  browserInfo: z.record(z.unknown()).nullable(),
  status: FeedbackStatusSchema,
  adminNotes: z.string().nullable(),
  resolvedBy: FeedbackUserSchema.nullable(),
  resolvedAt: z.string().nullable(),
  attachments: z.array(z.object({
    id: z.string().uuid(),
    filename: z.string(),
    fileSize: z.number().int().nonnegative(),
    contentType: z.string(),
    createdAt: z.string(),
  })),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeedbackItem = z.infer<typeof FeedbackItemSchema>;

export const FeedbackListResponseSchema = z.object({
  items: z.array(FeedbackListItemSchema),
  nextCursor: z.string().uuid().nullable(),
});
export type FeedbackListResponse = z.infer<typeof FeedbackListResponseSchema>;

export const CreateFeedbackInputSchema = z.object({
  description: z.string().min(10).max(10000),
  pageUrl: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
  browserInfo: z.record(z.unknown()).nullable().optional(),
});
export type CreateFeedbackInput = z.infer<typeof CreateFeedbackInputSchema>;

export const UpdateFeedbackInputSchema = z.object({
  status: FeedbackStatusSchema.optional(),
  adminNotes: z.string().nullable().optional(),
});
export type UpdateFeedbackInput = z.infer<typeof UpdateFeedbackInputSchema>;

// Log Schemas
export const LogEntrySchema = z.object({
  timestamp: z.string(),
  level: z.string(),
  service: z.string(),
  event: z.string().nullable(),
  message: z.string(),
  raw: z.record(z.unknown()),
});
export type LogEntry = z.infer<typeof LogEntrySchema>;

export const LogListResponseSchema = z.object({
  items: z.array(LogEntrySchema),
  nextCursor: z.string().nullable(),
});
export type LogListResponse = z.infer<typeof LogListResponseSchema>;

// CriteriaTemplate Schemas
export const CriteriaTemplateItemSchema = z.object({
  id: z.string().uuid(),
  criteriaTemplateId: z.string().uuid(),
  key: z.string(),
  label: z.string().min(1),
  llmDescription: z.string(),
  humanDescription: z.string(),
  color: z.string(),
  category: CriteriaCategorySchema.nullable().optional(),  // Category for highlight colors
  sortIndex: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type CriteriaTemplateItem = z.infer<typeof CriteriaTemplateItemSchema>;

export const CriteriaTemplateSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid(),
  name: z.string().min(1).max(255),
  paperType: PaperTypeSchema.nullable(),
  grades: z.array(z.number().int().min(3).max(12)).nullable(),
  isDefaultForType: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  criteriaCount: z.number().int().nonnegative().optional(),
});
export type CriteriaTemplate = z.infer<typeof CriteriaTemplateSchema>;

export const CriteriaTemplateWithCriteriaSchema = CriteriaTemplateSchema.extend({
  criteria: z.array(CriteriaTemplateItemSchema),
});
export type CriteriaTemplateWithCriteria = z.infer<typeof CriteriaTemplateWithCriteriaSchema>;

export const CriteriaTemplateListSchema = z.object({
  items: z.array(CriteriaTemplateSchema),
});
export type CriteriaTemplateList = z.infer<typeof CriteriaTemplateListSchema>;

export const CreateCriteriaTemplateInputSchema = z.object({
  name: z.string().min(1).max(255),
  paperType: PaperTypeSchema.nullable().optional(),
  grades: z.array(z.number().int().min(3).max(12)).nullable().optional(),
  isDefaultForType: z.boolean().optional().default(false),
  criteria: z.array(z.object({
    key: z.string(),
    label: z.string().min(1),
    llmDescription: z.string(),
    humanDescription: z.string(),
    color: z.string(),
    category: CriteriaCategorySchema.nullable().optional(),
    sortIndex: z.number().int().nonnegative().optional().default(0),
  })).optional().default([]),
});
export type CreateCriteriaTemplateInput = z.infer<typeof CreateCriteriaTemplateInputSchema>;

export const UpdateCriteriaTemplateInputSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  paperType: PaperTypeSchema.nullable().optional(),
  grades: z.array(z.number().int().min(3).max(12)).nullable().optional(),
  isDefaultForType: z.boolean().optional(),
});
export type UpdateCriteriaTemplateInput = z.infer<typeof UpdateCriteriaTemplateInputSchema>;

export const ApplyCriteriaTemplateInputSchema = z.object({
  criteriaTemplateId: z.string().uuid(),
});
export type ApplyCriteriaTemplateInput = z.infer<typeof ApplyCriteriaTemplateInputSchema>;

export const SaveAsCriteriaTemplateInputSchema = z.object({
  name: z.string().min(1).max(255),
  paperType: PaperTypeSchema.nullable().optional(),
  grades: z.array(z.number().int().min(3).max(12)).nullable().optional(),
  isDefaultForType: z.boolean().optional().default(false),
});
export type SaveAsCriteriaTemplateInput = z.infer<typeof SaveAsCriteriaTemplateInputSchema>;

// Response schema aliases for API responses
export const CriteriaTemplateResponseSchema = CriteriaTemplateWithCriteriaSchema;
export type CriteriaTemplateResponse = CriteriaTemplateWithCriteria;

// Criteria Template Generation Response (for LLM-generated criteria)
export const CriteriaTemplateGenerateResponseSchema = z.object({
  criteria: z.array(CriteriaTemplateItemSchema).min(1).max(10),
});
export type CriteriaTemplateGenerateResponse = z.infer<typeof CriteriaTemplateGenerateResponseSchema>;

export const ListCriteriaTemplatesResponseSchema = CriteriaTemplateListSchema;
export type ListCriteriaTemplatesResponse = CriteriaTemplateList;

// Template Schemas (for combined starter + user templates)
export const TemplateCriterionSchema = z.object({
  key: z.string(),
  label: z.string().min(1),
  llmDescription: z.string(),
  humanDescription: z.string(),
  color: z.string(),
  sortIndex: z.number().int().nonnegative(),
});
export type TemplateCriterion = z.infer<typeof TemplateCriterionSchema>;

export const TemplateSchema = z.object({
  id: z.string().uuid().nullable(),  // null for starter templates
  name: z.string(),
  paperType: PaperTypeSchema.nullable(),
  grades: z.array(z.number().int().min(3).max(12)).nullable(),
  criteriaCount: z.number().int().nonnegative(),
  isStarter: z.boolean(),
  isDefaultForType: z.boolean(),
  criteria: z.array(TemplateCriterionSchema),
});
export type Template = z.infer<typeof TemplateSchema>;

export const TemplateListSchema = z.object({
  items: z.array(TemplateSchema),
});
export type TemplateList = z.infer<typeof TemplateListSchema>;

// Feature Flag Schemas
export const FeatureFlagSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  description: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>;

export const FeatureFlagListSchema = z.object({
  items: z.array(FeatureFlagSchema),
});
export type FeatureFlagList = z.infer<typeof FeatureFlagListSchema>;

// Feature Flag with set status (for admin console)
export const FeatureFlagWithStatusSchema = z.object({
  key: z.string(),
  enabled: z.boolean(),
  description: z.string().nullable().optional(),
  isSet: z.boolean(),
  defaultValue: z.boolean(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});
export type FeatureFlagWithStatus = z.infer<typeof FeatureFlagWithStatusSchema>;

export const FeatureFlagRegistryListSchema = z.object({
  items: z.array(FeatureFlagWithStatusSchema),
});
export type FeatureFlagRegistryList = z.infer<
  typeof FeatureFlagRegistryListSchema
>;

// Email Template Schemas (user-level, not class-level)
export const EmailTemplateSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1).max(255),
  subject: z.string().min(1).max(512),
  body: z.string().min(1),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EmailTemplate = z.infer<typeof EmailTemplateSchema>;

export const EmailTemplateListSchema = z.object({
  items: z.array(EmailTemplateSchema),
});
export type EmailTemplateList = z.infer<typeof EmailTemplateListSchema>;

export const CreateEmailTemplateInputSchema = z.object({
  name: z.string().min(1).max(255),
  subject: z.string().min(1).max(512),
  body: z.string().min(1),
});
export type CreateEmailTemplateInput = z.infer<typeof CreateEmailTemplateInputSchema>;

export const UpdateEmailTemplateInputSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  subject: z.string().min(1).max(512).optional(),
  body: z.string().min(1).optional(),
});
export type UpdateEmailTemplateInput = z.infer<typeof UpdateEmailTemplateInputSchema>;

// Backward compatibility aliases (deprecated, will be removed)
/** @deprecated Use EmailTemplateSchema instead */
export const ClassEmailTemplateSchema = EmailTemplateSchema;
/** @deprecated Use EmailTemplate instead */
export type ClassEmailTemplate = EmailTemplate;
/** @deprecated Use EmailTemplateListSchema instead */
export const ClassEmailTemplateListSchema = EmailTemplateListSchema;
/** @deprecated Use EmailTemplateList instead */
export type ClassEmailTemplateList = EmailTemplateList;
/** @deprecated Use CreateEmailTemplateInputSchema instead */
export const CreateClassEmailTemplateInputSchema = CreateEmailTemplateInputSchema;
/** @deprecated Use CreateEmailTemplateInput instead */
export type CreateClassEmailTemplateInput = CreateEmailTemplateInput;
/** @deprecated Use UpdateEmailTemplateInputSchema instead */
export const UpdateClassEmailTemplateInputSchema = UpdateEmailTemplateInputSchema;
/** @deprecated Use UpdateClassEmailTemplateInput instead */
export type UpdateClassEmailTemplateInput = UpdateEmailTemplateInput;

export const RenderEmailResponseSchema = z.object({
  subject: z.string(),
  body: z.string(),
  studentEmail: z.string().email().nullable().optional(),
});
export type RenderEmailResponse = z.infer<typeof RenderEmailResponseSchema>;

// Available template variables for email templates
export const TEMPLATE_VARIABLES = [
  "student_name",
  "student_email",
  "share_link",
  "student_portal_link",
  "assignment_title",
  "teacher_name",
] as const;
export type TemplateVariable = typeof TEMPLATE_VARIABLES[number];

// =============================================================================
// Student Portal Schemas
// =============================================================================

export const PortalPaperSchema = z.object({
  documentId: z.string().uuid(),
  createdAt: z.string(),
  status: DocumentStatusSchema,
  hasAssessment: z.boolean(),
});
export type PortalPaper = z.infer<typeof PortalPaperSchema>;

export const PortalAssignmentSchema = z.object({
  assignmentId: z.string().uuid(),
  assignmentTitle: z.string(),
  papers: z.array(PortalPaperSchema),
  // Word count requirements
  minWordCount: z.number().int().nullable().optional(),
  maxWordCount: z.number().int().nullable().optional(),
  writingDocumentId: z.string().uuid().nullable().optional(),
  writingVersionStatus: DocumentVersionStatusSchema.nullable().optional(),
  // Google Classroom connection status
  isGcConnected: z.boolean().default(false),
});
export type PortalAssignment = z.infer<typeof PortalAssignmentSchema>;

export const PortalClassSchema = z.object({
  classId: z.string().uuid(),
  className: z.string(),
  teacherName: z.string(),
  assignments: z.array(PortalAssignmentSchema),
});
export type PortalClass = z.infer<typeof PortalClassSchema>;

export const StudentPortalResponseSchema = z.object({
  studentName: z.string(),
  studentFirstName: z.string().nullable().optional(),
  teacherName: z.string(),
  classes: z.array(PortalClassSchema),
});
export type StudentPortalResponse = z.infer<typeof StudentPortalResponseSchema>;

export const UpdateCompletedHighlightsInputSchema = z.object({
  completedHighlightIds: z.array(z.string()),
});
export type UpdateCompletedHighlightsInput = z.infer<typeof UpdateCompletedHighlightsInputSchema>;

export const PortalProgressionResponseSchema = z.object({
  assignmentId: z.string().uuid(),
  assignmentTitle: z.string(),
  papers: z.array(PortalPaperSchema),
});
export type PortalProgressionResponse = z.infer<typeof PortalProgressionResponseSchema>;

// =============================================================================
// Portal Writing Mode Schemas
// =============================================================================

// Resolved highlight with body hash for unresolve capability
// (Defined first as it's used by other schemas below)
export const ResolvedHighlightSchema = z.object({
  highlightId: z.string(),
  resolvedAt: z.string(), // ISO 8601 datetime
  bodyHashAtResolution: z.string(), // SHA256 hash of body_text when resolved
});
export type ResolvedHighlight = z.infer<typeof ResolvedHighlightSchema>;

// Lightweight version schema for assignment writing response
export const PortalAssignmentWritingVersionSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  versionNumber: z.number().int(),
  bodyJson: z.record(z.string(), z.unknown()).nullable().optional(),
  status: DocumentVersionStatusSchema,
  parentVersionId: z.string().uuid().nullable().optional(),
  inheritedHighlightIds: z.array(z.string()).nullable().optional(),
  // Full highlight data for inherited highlights (filtered to only inherited ones)
  inheritedHighlights: z.array(ShareTagSchema).nullable().optional(),
  // Criteria palette for styling inherited highlights
  tagPalette: z.array(ShareTagDefinitionSchema).nullable().optional(),
  resolvedHighlights: z.array(ResolvedHighlightSchema).nullable().optional(),
});
export type PortalAssignmentWritingVersion = z.infer<typeof PortalAssignmentWritingVersionSchema>;

export const PortalAssignmentWritingResponseSchema = z.object({
  assignmentId: z.string().uuid(),
  assignmentTitle: z.string(),
  assignmentDescription: z.string().nullable().optional(),
  minWordCount: z.number().int().nullable().optional(),
  maxWordCount: z.number().int().nullable().optional(),
  className: z.string(),
  teacherName: z.string(),
  documentId: z.string().uuid().nullable().optional(),
  currentVersionId: z.string().uuid().nullable().optional(),
  currentVersionStatus: DocumentVersionStatusSchema.nullable().optional(),
  // Full version data for initializing editor (when document exists)
  currentVersion: PortalAssignmentWritingVersionSchema.nullable().optional(),
  // Google Classroom connection status (for gating student writing)
  isGcConnected: z.boolean().default(false),
  gcStudentWritingEnabled: z.boolean().default(false),
});
export type PortalAssignmentWritingResponse = z.infer<typeof PortalAssignmentWritingResponseSchema>;

export const PortalDocumentVersionSchema = z.object({
  id: z.string().uuid(),
  documentId: z.string().uuid(),
  versionNumber: z.number().int(),
  bodyJson: z.record(z.string(), z.unknown()).nullable().optional(),
  bodyText: z.string().nullable().optional(),
  status: DocumentVersionStatusSchema,
  submittedAt: z.string().nullable().optional(),
  feedbackSharedAt: z.string().nullable().optional(),
  parentVersionId: z.string().uuid().nullable().optional(),
  inheritedHighlightIds: z.array(z.string()).nullable().optional(),
  resolvedHighlights: z.array(ResolvedHighlightSchema).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PortalDocumentVersion = z.infer<typeof PortalDocumentVersionSchema>;

export const PortalCreateDocumentResponseSchema = z.object({
  documentId: z.string().uuid(),
  version: PortalDocumentVersionSchema,
});
export type PortalCreateDocumentResponse = z.infer<typeof PortalCreateDocumentResponseSchema>;

export const PortalVersionUpdateInputSchema = z.object({
  bodyJson: z.record(z.string(), z.unknown()),
  resolvedHighlights: z.array(ResolvedHighlightSchema).nullable().optional(),
});
export type PortalVersionUpdateInput = z.infer<typeof PortalVersionUpdateInputSchema>;

export const PortalVersionSubmitResponseSchema = z.object({
  version: PortalDocumentVersionSchema,
  message: z.string(),
  // For GC-connected assignments, indicates submission was attached to GC
  gcAttached: z.boolean().optional().default(false),
  // If GC attached, this contains the Google Doc URL for the student
  gcDocUrl: z.string().nullable().optional(),
});
export type PortalVersionSubmitResponse = z.infer<typeof PortalVersionSubmitResponseSchema>;

// =============================================================================
// Edit Event Schemas (AI Detection)
// =============================================================================

export const EditEventTypeSchema = z.enum([
  "insert",
  "delete",
  "replace",
  "paste",
  "undo",
  "redo",
]);
export type EditEventType = z.infer<typeof EditEventTypeSchema>;

/**
 * Schema for a single ProseMirror step.
 * Steps are the canonical representation of document transforms.
 * See: https://prosemirror.net/docs/ref/#transform.Step
 *
 * Uses passthrough() to preserve additional fields for step types that need them:
 * - ReplaceAroundStep: gapFrom, gapTo, insert, structure
 * - Future step types may have other fields
 */
export const ProseMirrorStepSchema = z
  .object({
    /** Step type: 'replace', 'replaceAround', 'addMark', 'removeMark' */
    stepType: z.string(),
    /** Start position (0-indexed in ProseMirror) */
    from: z.number().int().nonnegative(),
    /** End position */
    to: z.number().int().nonnegative(),
    /** Slice content for replace steps (JSON representation) */
    slice: z.record(z.string(), z.unknown()).nullable().optional(),
    /** Mark for addMark/removeMark steps */
    mark: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .passthrough();
export type ProseMirrorStep = z.infer<typeof ProseMirrorStepSchema>;

export const EditEventInputSchema = z.object({
  id: z.string(),
  timestamp: z.number().int(),
  type: EditEventTypeSchema,
  sessionId: z.string(),
  // Unified format fields for both portal and extension
  // revisionNumber: optional for portal (backend generates), required for extension (Google Docs)
  revisionNumber: z.number().int().nonnegative().optional(),
  googleAuthorId: z.string().optional(),

  // NEW: ProseMirror steps for rich text replay (Phase 1: optional, will become required)
  prosemirrorSteps: z.array(ProseMirrorStepSchema).optional(),

  // DEPRECATED: Plain text fields - kept for backward compatibility during Phase 1
  // These will be removed once all clients are updated to send prosemirrorSteps
  position: z.number().int().nonnegative().optional().default(0),
  length: z.number().int().nonnegative().optional().default(0),
  content: z.string().max(10000).nullable().optional(),
  deletedContent: z.string().max(10000).nullable().optional(),
  keystrokeIntervalMs: z.number().int().nonnegative().nullable().optional(),
});
export type EditEventInput = z.infer<typeof EditEventInputSchema>;

export const EditEventBatchUploadInputSchema = z.object({
  events: z.array(EditEventInputSchema).min(1).max(1000),
  sessionId: z.string(),
});
export type EditEventBatchUploadInput = z.infer<typeof EditEventBatchUploadInputSchema>;

export const EditEventBatchUploadResponseSchema = z.object({
  eventsReceived: z.number().int().nonnegative(),
  eventsDeduplicated: z.number().int().nonnegative(),
});
export type EditEventBatchUploadResponse = z.infer<typeof EditEventBatchUploadResponseSchema>;

// =============================================================================
// Edit Metrics Schemas (AI Detection Analysis)
// =============================================================================

export const TypingMetricsSchema = z.object({
  avgKeystrokeIntervalMs: z.number().nullable(),
  keystrokeIntervalStdDev: z.number().nullable(),
  wordsPerMinute: z.number().nullable(),
  totalKeystrokes: z.number().int().nonnegative(),
});
export type TypingMetrics = z.infer<typeof TypingMetricsSchema>;

export const PasteMetricsSchema = z.object({
  pasteCount: z.number().int().nonnegative(),
  totalPasteChars: z.number().int().nonnegative(),
  avgPasteSize: z.number().nullable(),
  largestPasteSize: z.number().int().nonnegative(),
  pasteToTypeRatio: z.number().nullable(),
});
export type PasteMetrics = z.infer<typeof PasteMetricsSchema>;

export const EditBehaviorMetricsSchema = z.object({
  totalEdits: z.number().int().nonnegative(),
  insertCount: z.number().int().nonnegative(),
  deleteCount: z.number().int().nonnegative(),
  replaceCount: z.number().int().nonnegative(),
  undoCount: z.number().int().nonnegative(),
  redoCount: z.number().int().nonnegative(),
  errorCorrectionRate: z.number().nullable(),
});
export type EditBehaviorMetrics = z.infer<typeof EditBehaviorMetricsSchema>;

export const SessionMetricsSchema = z.object({
  totalSessionDurationMs: z.number().int().nonnegative(),
  activeWritingDurationMs: z.number().int().nonnegative(),
  idleTimeMs: z.number().int().nonnegative(),
  sessionCount: z.number().int().nonnegative(),
});
export type SessionMetrics = z.infer<typeof SessionMetricsSchema>;

export const BurstMetricsSchema = z.object({
  burstCount: z.number().int().nonnegative(),
  avgBurstLength: z.number().nullable(),
  avgBurstDurationMs: z.number().nullable(),
});
export type BurstMetrics = z.infer<typeof BurstMetricsSchema>;

export const EditMetricsResponseSchema = z.object({
  versionId: z.string().uuid(),
  typing: TypingMetricsSchema,
  paste: PasteMetricsSchema,
  editBehavior: EditBehaviorMetricsSchema,
  session: SessionMetricsSchema,
  burst: BurstMetricsSchema,
  totalEvents: z.number().int().nonnegative(),
  finalWordCount: z.number().int().nonnegative(),
  firstEventAt: z.string().nullable(),
  lastEventAt: z.string().nullable(),
  calculatedAt: z.string(),
});
export type EditMetricsResponse = z.infer<typeof EditMetricsResponseSchema>;

// =============================================================================
// SSE Progress Events (for real-time highlighting progress)
// =============================================================================

export const ProgressEventTypeSchema = z.enum([
  "connected",      // SSE connection established
  "started",        // Highlighting job started
  "criterion",      // Completed analyzing one focus area
  "summary",        // Generating feedback summary
  "deduplication",  // Finalizing highlights
  "completed",      // Highlighting finished successfully
  "failed",         // Highlighting failed
  "timeout",        // Connection timed out
  "error",          // Connection error
]);
export type ProgressEventType = z.infer<typeof ProgressEventTypeSchema>;

export const ProgressEventSchema = z.object({
  type: ProgressEventTypeSchema,
  documentId: z.string().uuid(),
  timestamp: z.string(),
  wordsGenerated: z.number().int().nonnegative(),
  stage: z.string().optional(),
  errorMessage: z.string().optional(),
});
export type ProgressEvent = z.infer<typeof ProgressEventSchema>;

// =============================================================================
// Google Classroom Schemas
// =============================================================================

export const GCCourseSchema = z.object({
  id: z.string(),
  name: z.string(),
  section: z.string().nullable(),
  description_heading: z.string().nullable(),
  course_state: z.string().nullable(),
  already_imported: z.boolean(),
});
export type GCCourse = z.infer<typeof GCCourseSchema>;

export const GCClassOutSchema = z.object({
  id: z.string(),
  name: z.string(),
  gc_course_id: z.string(),
  default_ela_grade_level: z.string().nullable(),
  student_count: z.number().int().nonnegative().nullable(),
});
export type GCClassOut = z.infer<typeof GCClassOutSchema>;

export const GCSyncResultSchema = z.object({
  students: z.number().int().nonnegative(),
  assignments: z.number().int().nonnegative(),
  submissions: z.number().int().nonnegative(),
});
export type GCSyncResult = z.infer<typeof GCSyncResultSchema>;

export const GCStatusSchema = z.object({
  gc_mode: z.boolean(),
  gc_mode_enabled_at: z.string().nullable(),
  has_refresh_token: z.boolean(),
  has_write_scopes: z.boolean(),
  has_ejected_data: z.boolean(),
});
export type GCStatus = z.infer<typeof GCStatusSchema>;

// =============================================================================
// Extension/AI Detector Schemas
// =============================================================================

export const RiskLevelSchema = z.enum(["none", "low", "medium", "high"]);
export type RiskLevel = z.infer<typeof RiskLevelSchema>;

export const ExtensionEditMetricsSchema = z.object({
  typing: TypingMetricsSchema,
  paste: PasteMetricsSchema,
  editBehavior: EditBehaviorMetricsSchema,
  session: SessionMetricsSchema,
  burst: BurstMetricsSchema,
  totalEvents: z.number().int().nonnegative(),
  finalWordCount: z.number().int().nonnegative(),
  firstEventAt: z.string().nullable(),
  lastEventAt: z.string().nullable(),
  calculatedAt: z.string(),
});
export type ExtensionEditMetrics = z.infer<typeof ExtensionEditMetricsSchema>;

export const ExtensionDocumentSummarySchema = z.object({
  id: z.string(),
  googleDocId: z.string(),
  googleDocUrl: z.string().nullable(),
  title: z.string(),
  ownerEmail: z.string().nullable(),
  wordCount: z.number().int().nonnegative(),
  riskLevel: RiskLevelSchema,
  hasInsufficientData: z.boolean(),
  isMultiAuthor: z.boolean(),
  authorCount: z.number().int().nonnegative(),
  importedAt: z.string(),
});
export type ExtensionDocumentSummary = z.infer<typeof ExtensionDocumentSummarySchema>;

export const ExtensionDocumentListResponseSchema = z.object({
  documents: z.array(ExtensionDocumentSummarySchema),
  total: z.number().int().nonnegative(),
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
});
export type ExtensionDocumentListResponse = z.infer<typeof ExtensionDocumentListResponseSchema>;

export const ExtensionDocumentDetailResponseSchema = z.object({
  id: z.string(),
  googleDocId: z.string(),
  googleDocUrl: z.string().nullable(),
  title: z.string(),
  ownerEmail: z.string().nullable(),
  wordCount: z.number().int().nonnegative(),
  riskLevel: RiskLevelSchema,
  hasInsufficientData: z.boolean(),
  isMultiAuthor: z.boolean(),
  authorCount: z.number().int().nonnegative(),
  metrics: ExtensionEditMetricsSchema.nullable(),
  importedAt: z.string(),
  lastAnalyzedAt: z.string().nullable(),
});
export type ExtensionDocumentDetailResponse = z.infer<typeof ExtensionDocumentDetailResponseSchema>;

export const ExtensionEditEventSchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  timestamp: z.string(),
  type: z.string(),
  authorEmail: z.string().nullable(),
  // NEW: ProseMirror steps for rich text replay
  prosemirrorSteps: z.array(ProseMirrorStepSchema).nullable().optional(),
  // DEPRECATED: Plain text fields - kept for backward compatibility
  keystrokeIntervalMs: z.number().int().nonnegative().nullable().optional(),
  position: z.number().int().nonnegative().optional(),
  length: z.number().int().nonnegative().optional(),
  content: z.string().nullable().optional(),
  deletedContent: z.string().nullable().optional(),
});
export type ExtensionEditEvent = z.infer<typeof ExtensionEditEventSchema>;

export const ExtensionEditEventsResponseSchema = z.object({
  documentId: z.string(),
  events: z.array(ExtensionEditEventSchema),
  total: z.number().int().nonnegative(),
  initialText: z.string().nullable().optional(),
});
export type ExtensionEditEventsResponse = z.infer<typeof ExtensionEditEventsResponseSchema>;

export const ExtensionLicenseStatusResponseSchema = z.object({
  hasLicense: z.boolean(),
  licenseType: z.string().nullable(),
  docsAnalyzed: z.number().int().nonnegative(),
  docsLimit: z.number().int().nonnegative().nullable(),
  docsRemaining: z.number().int().nonnegative().nullable(),
});
export type ExtensionLicenseStatusResponse = z.infer<typeof ExtensionLicenseStatusResponseSchema>;

// =============================================================================
// Miscellaneous API Response Schemas
// =============================================================================

export const UpgradeToAnnualResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type UpgradeToAnnualResponse = z.infer<typeof UpgradeToAnnualResponseSchema>;

export const MessageResponseSchema = z.object({
  message: z.string(),
});
export type MessageResponse = z.infer<typeof MessageResponseSchema>;

export const FeedbackAttachmentResponseSchema = z.object({
  id: z.string(),
  filename: z.string(),
  fileSize: z.number().int().nonnegative(),
  contentType: z.string(),
  createdAt: z.string(),
});
export type FeedbackAttachmentResponse = z.infer<typeof FeedbackAttachmentResponseSchema>;

export const FeedbackAttachmentUrlResponseSchema = z.object({
  url: z.string().url(),
});
export type FeedbackAttachmentUrlResponse = z.infer<typeof FeedbackAttachmentUrlResponseSchema>;

export const DeleteCountResponseSchema = z.object({
  deletedCount: z.number().int().nonnegative(),
});
export type DeleteCountResponse = z.infer<typeof DeleteCountResponseSchema>;

export const StreamingTokenResponseSchema = z.object({
  token: z.string(),
  expiresIn: z.number().int().nonnegative(),
});
export type StreamingTokenResponse = z.infer<typeof StreamingTokenResponseSchema>;
